import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import authService from '../services/AuthService';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [website, setWebsite] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,32}$/;
  const passwordValid = PASSWORD_REGEX.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      setError(t('auth.passwordInvalid'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await authService.register(name, email, password, website);
      setEmailSent(true);
    } catch (err) {
      setError(err.message || t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const bgStyle = {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    p: 2,
  };

  if (emailSent) {
    return (
      <Box sx={bgStyle}>
        <Card sx={{ width: '100%', maxWidth: 420, p: 1 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
              Concordare
            </Typography>
            <Typography variant="h6" fontWeight={600} mt={2} mb={1}>
              Revisa tu email
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Te hemos enviado un enlace de verificación a <strong>{email}</strong>.
              Haz clic en él para activar tu cuenta.
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
              Si no ves el email, revisa tu carpeta de spam.
            </Alert>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              fullWidth
              sx={{ mt: 1, mb: 2 }}
            >
              Ir al inicio
            </Button>
            <Typography variant="body2">
              ¿Ya tienes cuenta?{' '}
              <Link component={RouterLink} to="/login" underline="hover">
                Iniciar sesión
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={bgStyle}>
      <Card sx={{ width: '100%', maxWidth: 420, p: 1 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} color="primary" textAlign="center" gutterBottom>
            Concordare
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            {t('auth.register')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label={t('auth.name')}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label={t('auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
            />
            <TextField
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              helperText={password && !passwordValid ? t('auth.passwordRequirements') : t('auth.passwordRequirements')}
              error={password.length > 0 && !passwordValid}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Honeypot: hidden from humans, bots fill it automatically */}
            <Box
              aria-hidden="true"
              sx={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 1, height: 1, overflow: 'hidden', pointerEvents: 'none' }}
            >
              <TextField
                label="Website"
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !name || !email || !password}
              fullWidth
              sx={{ mt: 1 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth.registerButton')
              )}
            </Button>
          </Box>

          <Typography variant="body2" textAlign="center" mt={3}>
            {t('auth.alreadyAccount')}{' '}
            <Link component={RouterLink} to="/login" underline="hover">
              {t('auth.loginHere')}
            </Link>
          </Typography>
          <Typography variant="body2" textAlign="center" mt={1}>
            <Link component={RouterLink} to="/" underline="hover" color="text.secondary">
              Volver al inicio
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
